import React from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Building2, ShieldCheck, CalendarCheck, HeartHandshake, Stethoscope, Home as HomeIcon,
  UserRound, Activity, ClipboardList, Eye, Target, Lock, Sparkles, Mail, Globe, MapPin, ArrowRight,
} from 'lucide-react';
import SEO from '../components/SEO';

const IMG_HERO = 'https://images.pexels.com/photos/7578797/pexels-photo-7578797.jpeg?auto=compress&cs=tinysrgb&w=1200';
const IMG_STORY = 'https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';
const IMG_PROVIDER = 'https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const WHY = [
  { icon: Search, title: 'Convenient Healthcare Discovery', text: 'Explore healthcare professional profiles and available services in one place.' },
  { icon: Building2, title: 'Clinic & Home Healthcare Options', text: 'Discover clinic consultations and home-visit services offered by participating healthcare professionals.' },
  { icon: ShieldCheck, title: 'Provider Verification', text: 'We review relevant professional details and documents as part of our provider verification process.' },
  { icon: CalendarCheck, title: 'Convenient Appointment Booking', text: 'Explore available appointment options and connect with healthcare professionals through our platform.' },
  { icon: HeartHandshake, title: 'Healthcare for Your Loved Ones', text: 'Find healthcare service options that may help you coordinate care for yourself and your family.' },
];

const SERVICES = [
  { icon: Stethoscope, title: 'Clinic Consultations', text: 'Discover doctors and explore clinic appointment options.' },
  { icon: HomeIcon, title: 'Doctor Home Visits', text: 'Find doctors offering home visits in their available service areas.' },
  { icon: UserRound, title: 'Home Nursing Services', text: 'Explore nursing support offered by participating healthcare professionals.' },
  { icon: Activity, title: 'Physiotherapy Services', text: 'Discover physiotherapists and explore their available services.' },
  { icon: ClipboardList, title: 'Healthcare Provider Listings', text: 'A platform for eligible healthcare professionals and clinics to present their profiles and services.' },
];

const VALUES = [
  { title: 'Trust', text: 'We value transparent provider information and responsible healthcare discovery.' },
  { title: 'Accessibility', text: 'We aim to make healthcare options easier to discover.' },
  { title: 'Transparency', text: 'We strive to communicate our services and processes clearly.' },
  { title: 'Convenience', text: 'We use technology to simplify healthcare discovery and appointment coordination.' },
  { title: 'Continuous Improvement', text: 'We work towards making the platform more useful for patients and healthcare professionals.' },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const Eyebrow = ({ children, light }) => (
  <span className={`font-semibold uppercase tracking-wider text-xs ${light ? 'text-purple-200' : 'text-[#3B1C73]'}`}>{children}</span>
);

const PrimaryBtn = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="inline-flex items-center gap-2 bg-[#3B1C73] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2c1457] transition-colors">
    {children} <ArrowRight className="w-4 h-4" />
  </Link>
);

const OutlineBtn = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="inline-flex items-center gap-2 border-2 border-[#3B1C73] text-[#3B1C73] px-6 py-3 rounded-full font-semibold hover:bg-purple-50 transition-colors">
    {children}
  </Link>
);

const IconCard = ({ icon: Icon, title, text }) => (
  <div className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`about-card-${slug(title)}`}>
    <div className="w-12 h-12 rounded-xl bg-purple-50 text-[#3B1C73] flex items-center justify-center mb-4 group-hover:bg-[#3B1C73] group-hover:text-white transition-colors">
      <Icon className="w-6 h-6" />
    </div>
    <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
  </div>
);

const Header = () => (
  <header className="border-b border-slate-100 bg-white/90 backdrop-blur sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" data-testid="about-logo-link">
        <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>Physign</span>
      </Link>
      <nav className="flex items-center gap-6 text-sm">
        <Link to="/" className="text-slate-600 hover:text-[#3B1C73] hidden sm:inline" data-testid="about-nav-home">Home</Link>
        <Link to="/search" className="text-slate-600 hover:text-[#3B1C73] hidden sm:inline" data-testid="about-nav-search">Find Doctors</Link>
        <Link to="/auth" className="bg-[#3B1C73] text-white px-4 py-2 rounded-full font-medium hover:bg-[#2c1457]" data-testid="about-nav-login">Login / Sign Up</Link>
      </nav>
    </div>
  </header>
);

const Hero = () => (
  <section className="bg-gradient-to-b from-purple-50/60 to-white px-6 py-16 md:py-24" data-testid="about-hero">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Eyebrow>About PHYSIGN</Eyebrow>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 leading-tight">Transforming Healthcare Access Across India</h1>
        <p className="text-lg font-semibold text-[#3B1C73] mt-5">Healthcare Made Simple, Trusted &amp; Accessible</p>
        <p className="text-slate-600 mt-4 leading-relaxed">PHYSIGN is a healthcare technology platform connecting patients with doctors, nurses, physiotherapists and other healthcare professionals for clinic consultations and home healthcare services.</p>
        <p className="text-slate-600 mt-3 leading-relaxed">We aim to make healthcare discovery and appointment booking more convenient by bringing healthcare professionals and patients closer through technology.</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <PrimaryBtn to="/search" testId="about-hero-find-doctor">Find a Doctor</PrimaryBtn>
          <OutlineBtn to="/provider-signup" testId="about-hero-join-provider">Join as a Healthcare Provider</OutlineBtn>
        </div>
      </div>
      <img src={IMG_HERO} alt="Doctor consulting a patient" className="w-full h-[360px] md:h-[460px] object-cover rounded-3xl shadow-xl" />
    </div>
  </section>
);

const WhoWeAre = () => (
  <section className="px-6 py-16 md:py-20" data-testid="about-who-we-are">
    <div className="max-w-4xl mx-auto">
      <Eyebrow>Who We Are</Eyebrow>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">Finding the right healthcare professional should be simple and convenient.</h2>
      <div className="space-y-4 text-slate-600 leading-relaxed">
        <p>At PHYSIGN, we help patients discover healthcare professionals, explore available services and connect with providers for clinic consultations and home healthcare.</p>
        <p>Whether you are looking for a doctor, nursing support or physiotherapy services, PHYSIGN aims to simplify the process of finding suitable healthcare options for yourself and your loved ones.</p>
        <p>We are building a technology-enabled healthcare ecosystem focused on convenience, accessibility and trust.</p>
      </div>
    </div>
  </section>
);

const VisionMission = () => (
  <section className="px-6 pb-16 md:pb-20" data-testid="about-vision-mission">
    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
      <div className="bg-slate-50 p-8 md:p-10 rounded-3xl">
        <Eye className="w-8 h-8 text-[#3B1C73] mb-4" />
        <h3 className="text-2xl font-bold text-slate-900 mb-3">Our Vision</h3>
        <p className="text-slate-600 leading-relaxed">To become one of India&apos;s most trusted healthcare platforms, helping people discover healthcare professionals and access appropriate care more conveniently.</p>
      </div>
      <div className="bg-[#3B1C73] text-white p-8 md:p-10 rounded-3xl">
        <Target className="w-8 h-8 text-purple-200 mb-4" />
        <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
        <p className="leading-relaxed text-purple-50">To simplify healthcare access by connecting patients with healthcare professionals through a seamless digital experience for clinic consultations and home healthcare services.</p>
      </div>
    </div>
  </section>
);

const CardGrid = ({ id, eyebrow, title, items, note, bg }) => (
  <section className={`px-6 py-16 md:py-20 ${bg}`} data-testid={`about-${id}`}>
    <div className="max-w-7xl mx-auto">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-10">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((it) => <IconCard key={it.title} {...it} />)}
      </div>
      {note && <p className="text-sm text-slate-500 mt-8 italic">{note}</p>}
    </div>
  </section>
);

const Trust = () => (
  <section className="px-6 py-16 md:py-20" data-testid="about-trust">
    <div className="max-w-5xl mx-auto bg-gradient-to-br from-[#3B1C73] to-[#2c1457] rounded-3xl p-8 md:p-12 text-white">
      <div className="flex gap-3 mb-4"><ShieldCheck className="w-8 h-8 text-purple-200" /><Lock className="w-8 h-8 text-purple-200" /></div>
      <Eyebrow light>Trust, Verification &amp; Privacy</Eyebrow>
      <h2 className="text-2xl md:text-3xl font-bold mt-2 mb-5">Trust is an important part of the healthcare discovery experience.</h2>
      <div className="space-y-4 text-purple-50 leading-relaxed">
        <p>PHYSIGN reviews relevant professional information and documents as part of its provider verification process. We aim to provide clear profile information so users can make informed decisions when exploring healthcare professionals.</p>
        <p>We also recognise the importance of protecting personal information and aim to use appropriate safeguards for data handled through our platform.</p>
        <p>For more information, please read our <Link to="/privacy-policy" className="underline font-semibold hover:text-white" data-testid="about-privacy-link">Privacy Policy</Link>.</p>
      </div>
      <p className="text-xs text-purple-200 mt-6 italic">Provider verification does not guarantee clinical outcomes or replace independent medical judgement.</p>
    </div>
  </section>
);

const Story = () => (
  <section className="px-6 py-16 md:py-20 bg-slate-50" data-testid="about-story">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <img src={IMG_STORY} alt="Nurse caring for an elderly patient" className="w-full h-[340px] md:h-[420px] object-cover rounded-3xl" />
      <div>
        <Eyebrow>Our Story</Eyebrow>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">Finding healthcare should not be unnecessarily complicated.</h2>
        <div className="space-y-4 text-slate-600 leading-relaxed">
          <p>PHYSIGN was founded with a simple belief: finding healthcare should not be unnecessarily complicated.</p>
          <p>We recognised the importance of making it easier for people to discover healthcare professionals and explore suitable clinic and home healthcare options.</p>
          <p>This inspired us to build PHYSIGN—a technology-driven platform designed to make healthcare discovery more convenient for patients while helping healthcare professionals reach people who may need their services.</p>
          <p>Our journey is ongoing, and we are committed to improving the healthcare discovery experience as we grow.</p>
        </div>
      </div>
    </div>
  </section>
);

const ForProviders = () => (
  <section className="px-6 py-16 md:py-20" data-testid="about-for-providers">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Eyebrow>For Healthcare Professionals</Eyebrow>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">Grow Your Reach with PHYSIGN</h2>
        <div className="space-y-4 text-slate-600 leading-relaxed">
          <p>Are you a doctor, nurse, physiotherapist or clinic looking to present your services online?</p>
          <p>PHYSIGN provides eligible healthcare professionals with an opportunity to create a profile, showcase relevant professional information and connect with potential patients.</p>
          <p>Join our growing healthcare network and make your services easier to discover.</p>
        </div>
        <div className="mt-8"><PrimaryBtn to="/provider-signup" testId="about-register-provider">Register as a Provider</PrimaryBtn></div>
      </div>
      <img src={IMG_PROVIDER} alt="Doctor in clinic" className="w-full h-[340px] md:h-[420px] object-cover rounded-3xl order-first lg:order-last" />
    </div>
  </section>
);

const Values = () => (
  <section className="px-6 py-16 md:py-20 bg-purple-50/50" data-testid="about-values">
    <div className="max-w-7xl mx-auto">
      <Eyebrow>Our Core Values</Eyebrow>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-10">What guides us</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {VALUES.map((v, i) => (
          <div key={v.title} className="bg-white rounded-2xl p-6 border border-purple-100" data-testid={`about-value-${slug(v.title)}`}>
            <span className="text-3xl font-bold text-purple-200">0{i + 1}</span>
            <h3 className="font-semibold text-slate-900 mt-2 mb-2">{v.title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{v.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-14 max-w-3xl">
        <Sparkles className="w-7 h-7 text-[#3B1C73] mb-3" />
        <h3 className="text-2xl font-bold text-slate-900 mb-3">Our Commitment</h3>
        <p className="text-slate-600 leading-relaxed mb-3">At PHYSIGN, we believe technology can help make healthcare discovery more convenient and accessible.</p>
        <p className="text-slate-600 leading-relaxed mb-4">We are working towards a future where patients can explore healthcare options more easily and healthcare professionals can connect with the communities they serve.</p>
        <p className="text-lg font-semibold text-[#3B1C73]">PHYSIGN — Healthcare Made More Accessible.</p>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section className="px-6 py-16 md:py-20" data-testid="about-contact">
    <div className="max-w-5xl mx-auto text-center">
      <Eyebrow>Get in Touch</Eyebrow>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Have questions or want to learn more about PHYSIGN?</h2>
      <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-8 text-slate-600 mt-6">
        <span className="flex items-center justify-center gap-2"><Building2 className="w-4 h-4 text-[#3B1C73]" />PHYSIGN Technologies Pvt. Ltd.</span>
        <span className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4 text-[#3B1C73]" />Gurugram, Haryana, India</span>
        <a href="https://physign.com" className="flex items-center justify-center gap-2 hover:text-[#3B1C73]" data-testid="about-website-link"><Globe className="w-4 h-4 text-[#3B1C73]" />physign.com</a>
        <a href="mailto:info@physign.com" className="flex items-center justify-center gap-2 hover:text-[#3B1C73]" data-testid="about-email-link"><Mail className="w-4 h-4 text-[#3B1C73]" />info@physign.com</a>
      </div>
      <div className="flex flex-wrap justify-center gap-3 mt-10">
        <PrimaryBtn to="/contact" testId="about-cta-contact">Contact Us</PrimaryBtn>
        <OutlineBtn to="/search" testId="about-cta-find-doctor">Find a Doctor</OutlineBtn>
        <OutlineBtn to="/provider-signup" testId="about-cta-join">Join PHYSIGN</OutlineBtn>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-slate-100 py-6">
    <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
      <p>© 2026 Physign Technologies Pvt. Ltd. All rights reserved.</p>
      <div className="flex gap-5">
        <Link to="/terms" className="hover:text-[#3B1C73]">Terms</Link>
        <Link to="/privacy-policy" className="hover:text-[#3B1C73]">Privacy</Link>
        <Link to="/contact" className="hover:text-[#3B1C73]">Contact</Link>
      </div>
    </div>
  </footer>
);

const AboutPhysign = () => (
  <div className="min-h-screen bg-white" data-testid="about-page">
    <SEO
      path="/about"
      title="About Us - Healthcare Made Simple, Trusted & Accessible"
      description="PHYSIGN is a healthcare technology platform connecting patients with doctors, nurses, physiotherapists and clinics for clinic consultations and home healthcare services across India."
    />
    <Header />
    <Hero />
    <WhoWeAre />
    <VisionMission />
    <CardGrid id="why-choose" eyebrow="Why Choose PHYSIGN?" title="Healthcare discovery, made convenient" items={WHY} bg="bg-slate-50" />
    <CardGrid id="services" eyebrow="Our Healthcare Services" title="Care options in one place" items={SERVICES} bg="bg-white" note="Service availability depends on provider participation, location and appointment availability." />
    <Trust />
    <Story />
    <ForProviders />
    <Values />
    <Contact />
    <Footer />
  </div>
);

export default AboutPhysign;
